import { HttpParams } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AdminService, NamedLogin } from '../../../pages/admin/services/admin.service';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';
import { NotificationsService } from '../../../core/services/notifications.service';

@Component({
  selector: 'app-named-login-setup-modal',
  templateUrl: './named-login-setup-modal.component.html',
  styleUrls: ['./named-login-setup-modal.component.css'],
})
export class NamedLoginSetupModalComponent implements OnInit {
  @ViewChild('changePasswordForm') changePasswordForm: NgForm;

  formData: {
    NamedLogin: string;
    Password: string;
    ConfirmPassword: string;
  };
  passwordsMatch = true;
  addUserMode = false;
  changePasswordMode = false;
  performingAction = false;
  loginLink: string = window.location.origin + '/Account/NamedLogin';
  users: NamedLogin[] = [];

  constructor(
    private readonly adminService: AdminService,
    private readonly activeModalService: NgbActiveModal,
    private readonly modalService: NgbModal,
    private notificationService: NotificationsService,
  ) { }

  async ngOnInit (): Promise<void> {
    this.getNamedLoginList();
    this.formData = { NamedLogin: '', Password: '', ConfirmPassword: '' };
  }

  async getNamedLoginList (): Promise<void> {
    try {
      const response = await this.adminService.getNamedLoginList();
      if (!response.success) {
        this.notificationService.showError(response.error);
      } else {
        this.users = response.data;
      }
    } catch (error) {
      throw error;
    }
  }

  toggleAddUserMode (addUserMode: boolean): void {
    this.addUserMode = addUserMode;
  }

  async addUser (): Promise<void> {
    try {
      this.performingAction = true;
      const params = new HttpParams()
        .set('namedLogin', this.formData.NamedLogin)
        .set('password', this.formData.Password)
        .set('confirmPassword', this.formData.ConfirmPassword);

      const response = await this.adminService.createNamedLogin(params);
      if (!response.success) {
        this.notificationService.showError(response.error);
      } else {
        const name = this.formData.NamedLogin;
        this.resetForm();
        this.notificationService.showSuccess(`User ${name} has been added.`);
        this.getNamedLoginList();
      }
    } catch (error) {
      throw error;
    } finally {
      this.performingAction = false;
    }
  }

  async removeUser (user: NamedLogin): Promise<void> {
    try {
      const modalRef = this.modalService.open(ConfirmModalComponent, {
        size: 's',
      });
      modalRef.componentInstance.headerText = 'Update Manager';
      modalRef.componentInstance.bodyText = `Are you sure you want to remove the user ${user.NamedLogin}?`;
      modalRef.componentInstance.confirmButtontext = 'Remove';
      modalRef.componentInstance.cancelButtontext = 'Cancel';
      const result = await modalRef.result;
      if (result === true) {
        this.performingAction = true;
        const params = new HttpParams().set('NamedLogin', user.NamedLogin);
        const response = await this.adminService.removeNamedLogin(params);
        if (!response.success) {
          this.notificationService.showError(response.error);
        } else {
          this.notificationService.showSuccess(`User ${user.NamedLogin} has been removed.`);
          this.getNamedLoginList();
        }
      }
    } catch (error) {
      throw error;
    } finally {
      this.performingAction = false;
    }
  }

  async unlockUser (user: NamedLogin): Promise<void> {
    try {
      this.performingAction = true;
      const params = new HttpParams().set('userKeyID', user.UserKeyID);
      const response = await this.adminService.unlockUser(params);
      if (!response.success) {
        this.notificationService.showError(response.error);
      } else {
        this.notificationService.showSuccess(`User ${user.NamedLogin} has been unlocked.`);
        this.getNamedLoginList();
      }
    } catch (error) {
      throw error;
    } finally {
      this.performingAction = false;
    }
  }

  changePasswordEnable (user: NamedLogin): void {
    this.notificationService.clearNotifications();
    this.formData = { NamedLogin: user.NamedLogin, Password: '', ConfirmPassword: '' };
    this.changePasswordMode = true;
  }

  async changePassword (): Promise<void> {
    try {
      if (!this.changePasswordForm.invalid) {
        this.performingAction = true;
        const params = new HttpParams()
          .set('NamedLogin', this.formData.NamedLogin)
          .set('Password', this.formData.Password)
          .set('ConfirmPassword', this.formData.ConfirmPassword);

        const response = await this.adminService.changeNamedLoginPassword(params);
        if (!response.success) {
          this.notificationService.showError(response.error);
        } else {
          const user = this.formData.NamedLogin;
          this.notificationService.showSuccess(`Password for user ${user} has been changed.`);
          this.resetForm();
          this.getNamedLoginList();
        }
      }
    } catch (error) {
      throw error;
    } finally {
      this.performingAction = false;
    }
  }

  confirmPassword (): void {
    this.passwordsMatch = this.validatePasswords();
  }

  validatePasswords (): boolean {
    //do not mark an error if the confirm password hasn't been entered
    if (this.formData.ConfirmPassword == null || this.formData.ConfirmPassword == '') return true;

    const passwordsMatch = this.formData.ConfirmPassword === this.formData.Password;
    if (!passwordsMatch) {
      this.notificationService.showError(`Passwords must match!`, null);
    }
    return passwordsMatch;
  }

  clearErrors (): void {
    this.notificationService.clearNotifications();
    this.passwordsMatch = true;
  }

  resetForm (): void {
    this.formData = { NamedLogin: '', Password: '', ConfirmPassword: '' };
    this.toggleAddUserMode(false);
    this.changePasswordMode = false;
  }

  closeModal (): void {
    this.activeModalService.close();
  }
}
